// What the overlay's "Faces on this photo" section shows, and in which order.
// Pure: the grouping and the collapse rule only, no fetching (ADR-0039).
import { facePageNumber } from '@/components/faces/format'
import type { DocumentFaceCandidate, DocumentFacePerson } from '@/services/api/photos'

/** Suggested first — those are the rows that want a decision — then the rest. */
export const FACE_SECTION_ORDER = ['suggested', 'confirmed', 'unassigned'] as const

/** One row of the section: a face, in the order the section lists it. */
export interface FaceRow {
  faceId: string
  state: DocumentFacePerson['state']
  /** The page to name, from 1: a PDF's face only (`facePageNumber`). */
  pageNumber: number | null
  cropUrl: string | null
  /** The confirmed person; null unless the face is confirmed. */
  personId: string | null
  displayName: string | null
  photoUrl: string | null
  /**
   * Up to three stored candidates, strongest first. On a confirmed face each one
   * proposes merging the face's identity into the candidate's (ADR-0042).
   */
  candidates: DocumentFaceCandidate[]
  qualityFlags: string[]
  /**
   * The pass of a recording the face came from (PRD-0074): its span in seconds. Both null
   * for a photo's face.
   */
  tStart: number | null
  tEnd: number | null
}

/** The section's rows plus the faces folded behind the "N more faces" row. */
export interface FacesSection {
  rows: FaceRow[]
  /**
   * Every face as the API gave it, in the API's own order. The face layer draws
   * from this — a collapsed row is still a box on the photo, and the boxes need
   * the page and the source image the rows do not carry.
   */
  allFaces: DocumentFacePerson[]
  /**
   * Low-quality faces, kept out of the main list so a group photo's blur and
   * profile crops do not bury the faces worth deciding on. Empty when there are
   * none; the section reveals them behind one row.
   */
  collapsed: FaceRow[]
}

const STATE_RANK: Record<DocumentFacePerson['state'], number> = {
  suggested: 0,
  confirmed: 1,
  unassigned: 2,
}

function toRow(face: DocumentFacePerson, documentType: string | null | undefined): FaceRow {
  return {
    faceId: face.face_id,
    state: face.state,
    pageNumber: facePageNumber(face.page, documentType),
    cropUrl: face.crop_url,
    personId: face.person_id,
    displayName: face.display_name,
    photoUrl: face.photo_url,
    candidates: face.candidates,
    qualityFlags: face.quality_flags,
    tStart: face.t_start ?? null,
    tEnd: face.t_end ?? null,
  }
}

/**
 * A face is folded away only when nothing is at stake on it: it is flagged low
 * quality *and* undecided. A flagged face the analyst confirmed, or one the
 * matcher still suggests somebody for, stays in the list — the row is the only
 * place that decision can be taken back or taken at all.
 */
function isCollapsible(row: FaceRow): boolean {
  return row.qualityFlags.length > 0 && row.state === 'unassigned'
}

/**
 * Whether Identities leaves the face out of its Unassigned faces (PRD-0074 SC-008): a pass of a
 * recording (a row with a time span; the server asks `source = 'video'`, which every pass is)
 * with any quality warning is below the floor the server's unassigned read applies. A photo's
 * flagged face is still offered there, and a confirmed face is nobody's to assign.
 */
export function leftOutOfUnassigned(row: FaceRow): boolean {
  return row.tStart !== null && row.qualityFlags.length > 0 && row.state !== 'confirmed'
}

/** The strongest candidate's score, so a near-certain suggestion sorts first. */
function topScore(row: FaceRow): number {
  return row.candidates.length > 0 ? row.candidates[0].score : 0
}

/**
 * Within a state: the strongest suggestion first, then by page so a PDF reads
 * front to back — or by time so a recording reads start to end — and the face
 * id breaks the tie so the order never flickers between two reads.
 */
function compareRows(a: FaceRow, b: FaceRow): number {
  return (
    STATE_RANK[a.state] - STATE_RANK[b.state] ||
    topScore(b) - topScore(a) ||
    (a.pageNumber ?? 0) - (b.pageNumber ?? 0) ||
    (a.tStart ?? 0) - (b.tStart ?? 0) ||
    a.faceId.localeCompare(b.faceId)
  )
}

/**
 * Order the document's faces for the section (PRD-0031 slice 06).
 *
 * Suggested, confirmed and unassigned, in that order; the faces flagged low
 * quality and still undecided move behind the "N more faces" row instead of
 * padding a group photo's list.
 */
export function facesSection(
  faces: readonly DocumentFacePerson[] | undefined,
  documentType?: string | null
): FacesSection {
  const rows = (faces ?? []).map(face => toRow(face, documentType)).sort(compareRows)
  return {
    allFaces: [...(faces ?? [])],
    rows: rows.filter(row => !isCollapsible(row)),
    collapsed: rows.filter(isCollapsible),
  }
}

/** Every person the photo confirms, deduplicated — a face's link target (SC-005). */
export function confirmedPersonIds(faces: readonly DocumentFacePerson[] | undefined): string[] {
  const ids = new Set<string>()
  for (const face of faces ?? []) {
    if (face.state === 'confirmed' && face.person_id) ids.add(face.person_id)
  }
  return [...ids]
}
